import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useT } from '../../i18n';
import { restFetch } from './restui';

/* Private branded access — "Authorized Access Required". Not a Kitxens login. */

function Shell({ children, restaurant, osId, isEs, branding }) {
  const accent = branding?.primary_color || '#F77F11';
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4"
      style={{ fontFamily: 'Inter, system-ui, sans-serif', '--rest-accent': accent, '--rest-accent-soft': `${accent}14` }}>
      <div className="w-full max-w-[400px]">
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--rest-accent)] font-bold">Restaurant AI OS</p>
          {branding?.logo_url && (
            <img src={branding.logo_url} alt={restaurant} className="mx-auto mt-5 h-16 w-auto max-w-[200px] object-contain rounded-xl" data-testid="rest-login-logo" />
          )}
          <h1 className="mt-3 text-[26px] font-semibold tracking-tight text-gray-900" data-testid="rest-login-restaurant">{restaurant || (isEs ? 'Acceso Privado' : 'Private Access')}</h1>
          <p className="mt-1 text-[11.5px] text-gray-400">{isEs ? 'Workspace privado' : 'Private Workspace'}{osId ? ` · ${osId}` : ''}</p>
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1 text-[10.5px] uppercase tracking-wider text-gray-500 font-bold">
            🔒 {isEs ? 'Acceso autorizado requerido' : 'Authorized Access Required'}
          </p>
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-7">{children}</div>
        <p className="text-center mt-6 text-[10.5px] text-gray-400">Powered by <span className="text-gray-500 font-semibold">Kitxens AI</span></p>
      </div>
    </div>
  );
}

const inputCls = 'w-full rounded-lg bg-[#FAFAF8] border border-gray-200 px-3.5 py-2.5 text-[14px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[var(--rest-accent)]';
const btnCls = 'w-full rounded-lg bg-[var(--rest-accent)] hover:opacity-90 text-white font-bold text-[14px] py-2.5 transition-colors disabled:opacity-50';

function errText(d) {
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || '').join(' ');
  return d?.msg || 'Something went wrong';
}

export default function RestLogin() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const nav = useNavigate();
  const { slug, token } = useParams();
  const [inst, setInst] = useState(null);
  const [invite, setInvite] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [human, setHuman] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (slug) restFetch(`/instance/${slug}`).then((r) => (r.ok ? r.json() : null)).then(setInst).catch(() => {});
    if (token) restFetch(`/invite/${token}`).then(async (r) => {
      if (!r.ok) { setErr(isEs ? 'Invitación no válida o caducada' : 'Invitation invalid or expired'); return; }
      const j = await r.json();
      setInvite(j); setName(j.name || '');
    }).catch(() => {});
    // eslint-disable-next-line
  }, [slug, token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = token
        ? await restFetch(`/invite/${token}/accept`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ password, name, locale: lang }) })
        : await restFetch('/login', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password, human }) });
      const j = await r.json();
      if (!r.ok) { setErr(errText(j.detail)); return; }
      nav('/restaurant');
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  const restaurantName = invite?.restaurant || inst?.name;
  const osId = inst?.instance?.os_id;

  return (
    <Shell restaurant={restaurantName} osId={osId} isEs={isEs} branding={inst?.branding}>
      {token && invite && (
        <p className="mb-4 text-[12.5px] text-gray-500">
          {isEs ? 'Invitación para ' : 'Invitation for '}<b className="text-gray-900">{invite.email}</b> · {invite.role}
          <br />{isEs ? 'Crea tu contraseña para activar tu acceso.' : 'Create your password to activate your access.'}
        </p>
      )}
      <form onSubmit={submit} className="space-y-3">
        {token ? (
          <>
            <input data-testid="rest-invite-name" className={inputCls} placeholder={isEs ? 'Tu nombre' : 'Your name'} value={name} onChange={(e) => setName(e.target.value)} />
            <input data-testid="rest-invite-password" className={inputCls} type="password" placeholder={isEs ? 'Nueva contraseña (mín. 8)' : 'New password (min 8)'} value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
          </>
        ) : (
          <>
            <input data-testid="rest-login-email" className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <input data-testid="rest-login-password" className={inputCls} type="password" placeholder={isEs ? 'Contraseña' : 'Password'} value={password} onChange={(e) => setPassword(e.target.value)} required />
            <button type="button" data-testid="rest-login-human-check" onClick={() => setHuman(!human)}
              className={`w-full flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 text-[13px] transition-colors ${human ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-[#FAFAF8] text-gray-500 hover:border-gray-300'}`}>
              <span className={`w-[18px] h-[18px] rounded flex items-center justify-center border transition-colors ${human ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-gray-300 bg-white'}`}>{human ? '✓' : ''}</span>
              {human ? (isEs ? 'Verificado — eres humano' : 'Verified — you are human') : (isEs ? 'No soy un robot' : "I'm not a robot")}
            </button>
          </>
        )}
        {err && <p className="text-[12px] text-red-600" data-testid="rest-login-error">{err}</p>}
        <button data-testid="rest-login-submit" className={btnCls} disabled={busy}>
          {busy ? '…' : token ? (isEs ? 'Activar acceso' : 'Activate access') : (isEs ? 'Entrar' : 'Sign In')}
        </button>
      </form>
    </Shell>
  );
}
